/**
 * /vindecare/{slug} — SEO-indexable landing page for each "Cu ce te pot ajuta" item.
 * Renders headline + symptoms + approach steps + CTA, plus JSON-LD MedicalCondition/Service schema.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { resolveIcon } from "@/components/admin/IconPicker";
import SectionCta from "@/components/SectionCta";

export default function ProblemDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/problems/${slug}`)
      .then((r) => setP(r.data))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
    api.get("/public/problems")
      .then((r) => setRelated((r.data || []).filter((x) => x.slug !== slug).slice(0, 4)))
      .catch(() => setRelated([]));
    // Materialele din Bibliotecă etichetate cu această poiană
    api.get(`/public/library?pain=${encodeURIComponent(slug)}`)
      .then((r) => setResources((r.data?.items || []).slice(0, 3)))
      .catch(() => setResources([]));
  }, [slug]);

  if (loading) return <div className="py-32 text-center" style={{ color: "var(--ct-text-muted)" }}>Se încarcă...</div>;
  if (!p) return (
    <div className="py-32 text-center px-5">
      <h2 className="font-serif text-3xl mb-4">Pagină inexistentă</h2>
      <Link to="/" className="btn-secondary">Înapoi acasă</Link>
    </div>
  );

  const Icon = resolveIcon(p.icon);

  // JSON-LD: Service + MedicalTherapy combined for max SEO coverage
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": p.headline || p.name,
    "description": p.seo_description || p.summary || "",
    "provider": {
      "@type": "Person",
      "name": "Terapeut Georgiana",
      "url": typeof window !== "undefined" ? window.location.origin : "",
    },
    "areaServed": "Romania",
    "url": typeof window !== "undefined" ? `${window.location.origin}/vindecare/${p.slug}` : `/vindecare/${p.slug}`,
    ...(p.image_url ? { "image": p.image_url } : {}),
  };

  return (
    <div data-testid="problem-detail-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }} />

      {/* HERO */}
      <section className="py-14 sm:py-20 lg:py-24 relative overflow-hidden" style={{ background: "var(--ct-bg-soft)" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8 relative">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm mb-6 hover:gap-2 transition-all" style={{ color: "var(--ct-text-muted)" }}>
            <ChevronLeft size={14} /> Înapoi pe Acasă
          </Link>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full" style={{ background: "var(--ct-emerald)", color: "var(--ct-gold)" }}>
                <Icon size={14} />
                <span className="text-[11px] uppercase tracking-[0.22em]">Cu ce te pot ajuta</span>
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="problem-title">{p.headline || p.name}</h1>
              {p.summary && <p className="text-base sm:text-lg leading-relaxed mt-5 max-w-2xl" style={{ color: "var(--ct-text-muted)" }}>{p.summary}</p>}
              <div className="mt-7 flex flex-wrap gap-3">
                <SectionCta
                  content={p}
                  prefix="cta"
                  fallbackText={p.cta_label || "Programează o ședință"}
                  fallbackLink={p.cta_link || "/programare"}
                  className="btn-primary inline-flex items-center gap-2"
                />
                <SectionCta
                  content={p}
                  prefix="cta2"
                  fallbackText="Întreabă"
                  fallbackLink="/contact"
                  className="btn-secondary inline-flex items-center gap-2"
                />
              </div>
            </div>
            {(p.image_url || p.thumbnail_url) && (
              <div className="rounded-3xl overflow-hidden aspect-square" style={{ border: "1px solid var(--ct-border)" }}>
                <img src={p.image_url || p.thumbnail_url} alt={p.headline || p.name} className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* DESCRIPTION */}
      {p.description && (
        <section className="py-14 sm:py-20" style={{ background: "var(--ct-bg)" }}>
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <h2 className="font-serif text-2xl sm:text-3xl mb-5">Despre această temă</h2>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: "var(--ct-text-muted)", whiteSpace: "pre-line" }}>{p.description}</p>
          </div>
        </section>
      )}

      {/* SYMPTOMS */}
      {Array.isArray(p.symptoms) && p.symptoms.length > 0 && (
        <section className="py-14 sm:py-20" style={{ background: "var(--ct-bg-soft)" }} data-testid="problem-symptoms">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <span className="eyebrow">Cum se manifestă</span>
            <h2 className="font-serif text-2xl sm:text-3xl mt-3 mb-6">Semne că poate fi pentru tine</h2>
            <ul className="space-y-3">
              {p.symptoms.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-base">
                  <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--ct-gold)" }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* APPROACH */}
      {Array.isArray(p.approach) && p.approach.length > 0 && (
        <section className="py-14 sm:py-20" style={{ background: "var(--ct-bg)" }} data-testid="problem-approach">
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <div className="text-center mb-10">
              <span className="eyebrow">Cum lucrăm</span>
              <h2 className="font-serif text-2xl sm:text-3xl mt-3">Abordarea mea</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {p.approach.map((step, i) => (
                <div key={i} className="card-premium p-6" data-testid={`approach-step-${i}`}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center mb-4 font-serif text-base" style={{ background: "var(--ct-gold)", color: "#0D1B2A" }}>{i + 1}</div>
                  <h3 className="font-serif text-lg mb-2">{step.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--ct-text-muted)" }}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PASUL URMĂTOR — materiale din Bibliotecă pe această temă */}
      {resources.length > 0 && (
        <section className="py-14 sm:py-20" style={{ background: "var(--ct-bg-soft)" }} data-testid="problem-resources">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="text-center mb-10">
              <span className="eyebrow">Pasul următor</span>
              <h2 className="font-serif text-2xl sm:text-3xl mt-3">Materiale care te ajută cu {p.name?.toLowerCase() || "această temă"}</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {resources.map((card, i) => (
                <Link to={`/cursuri/${card.slug}`} key={card.id} className="card-premium overflow-hidden flex flex-col hover:-translate-y-0.5 transition-transform" data-testid={`problem-resource-${i}`}>
                  {card.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={card.cover_image} alt={card.title} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-serif text-lg leading-tight mb-1.5">{card.title}</h3>
                    <p className="text-xs leading-snug flex-1 line-clamp-2" style={{ color: "var(--ct-text-muted)" }}>{card.subtitle}</p>
                    <span className="mt-3 text-xs font-medium" style={{ color: "var(--ct-link)" }}>
                      {card.access_tier === "purchase" && card.price > 0 ? `${card.price} ${card.currency || "RON"}` : card.access_tier === "subscription" ? "Cu abonament" : "Gratuit"} · Deschide →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to={`/biblioteca?durere=${encodeURIComponent(slug)}`} className="btn-secondary inline-flex items-center gap-2" data-testid="problem-to-library">
                Vezi toată biblioteca pe această temă <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="py-14 sm:py-20" style={{ background: "var(--ct-emerald)", color: "var(--ct-text-inverse)" }}>
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl mb-4">Ești pregătit să începi vindecarea?</h2>
          <p className="text-sm sm:text-base mb-7 opacity-90">Prima discuție este pentru a vedea împreună dacă lucrul cu mine este potrivit pentru tine.</p>
          <SectionCta
            content={p}
            prefix="cta"
            fallbackText={p.cta_label || "Programează o ședință"}
            fallbackLink={p.cta_link || "/programare"}
            className="btn-gold inline-flex items-center gap-2 !text-base"
          />
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-14 sm:py-20" style={{ background: "var(--ct-bg)" }}>
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-center mb-10">
              <span className="eyebrow">Mai mult</span>
              <h2 className="font-serif text-2xl sm:text-3xl mt-3">Cu ce alte teme te mai pot ajuta</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r, i) => {
                const RIcon = resolveIcon(r.icon);
                return (
                  <Link to={`/vindecare/${r.slug}`} key={r.id} className="card-premium p-5 hover:-translate-y-0.5 transition-transform" data-testid={`related-${i}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: "var(--ct-emerald)" }}>
                      <RIcon size={16} style={{ color: "var(--ct-gold)" }} />
                    </div>
                    <h3 className="font-serif text-lg mb-1">{r.headline || r.name}</h3>
                    <p className="text-xs leading-snug line-clamp-2" style={{ color: "var(--ct-text-muted)" }}>{r.summary}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs" style={{ color: "var(--ct-link)" }}>
                      Vezi <ChevronRight size={12} />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
